import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor, orderBy } from '@progress/kendo-data-query';
import { Router, ActivatedRoute } from '@angular/router';
import { ReportService } from '../../services/report-menu.service';
import {
    ReportMenuModule,
    ReportMenuData,
    ReportMenuModuleItems,
    ReportMenuBookmarkForm,
} from '../../interfaces/report-menu-data';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';

@Component({
    selector: 'app-reports',
    encapsulation: ViewEncapsulation.None, // Remove the restriction on the parent component's ability to style child components (For Kendo Grid styling)
    templateUrl: './report-menu.component.html',
    styleUrls: ['./report-menu.component.scss'],
})
export class ReportMenuComponent implements OnInit {
    selectedModule: ReportMenuModule = null;
    allData: ReportMenuData[];
    selectedModuleData: ReportMenuData[];
    modules: ReportMenuModuleItems[];
    routerModule: string;
    doneLoadingModules = false;

    // Grid Options
    pageSizes = true;
    previousNext = true;
    pageSize = 10;
    skip = 0;
    sort: SortDescriptor[] = [];
    preservedSort: SortDescriptor[] = [];
    search: string;
    gridView: GridDataResult;

    //info = true;
    type: 'numeric' | 'input' = 'input';

    pageable = {
        info: true,
        type: this.type,
        pageSizes: true,
        previousNext: 'previousNext',
        //buttonCount: 2,
    };

    sortable = {
        mode: 'single',
        allowUnsort: true,
    };

    @ViewChild(TooltipDirective) tooltipDir: TooltipDirective;

    constructor(
        private _reportService: ReportService,
        private route: ActivatedRoute,
        private router: Router
    ) {}

    ngOnInit() {
        // Data for left-hand menu. Count is updated by getItemTotals()
        this.modules = [
            { name: 'All Reports', selected: true, count: 0, locked: false },
            { name: 'Bookmarked', selected: false, count: 0, locked: false },
        ];

        this.sort = [{ dir: 'asc', field: 'moduleName' }];

        this.preservedSort = [{ dir: 'desc', field: 'bookmarked' }];

        this.search = null;
        this.loadData();
        this.determineIfLocked();
    }

    // Calls service and pulls all report data w/ boolean for whether user has bookmarked
    // Calls service and returns modules
    loadData() {
        const order = [
            'Directory',
            'My Vision',
            'Leads & Partners',
            'Pipeline',
            'Production',
            'Performance',
            'Historical Trends',
            'Financials',
        ];

        this._reportService.getModules().subscribe((md) => {
            const modulesArray = JSON.parse(JSON.stringify(md));
            for (let module of modulesArray) {
                module.order = order.indexOf(module.name);
            }
            modulesArray.sort((a, b) => a.order - b.order);
            this.modules = this.modules.concat(modulesArray);

            this._reportService.getReports().subscribe((d) => {
                this.allData = d.filter((x) => x.locked == false);

                this.selectedModuleData = this.allData;

                let routerModule = this.route.params.subscribe((p) => {
                    if (p && p['module']) {
                        this.routerModule = p['module'];
                        this.setModuleFromRoute(p['module']);
                    }
                });

                this.updateGrid();
                this.getItemTotals();
            });
        });
    }

    // Filters current selected module data if search text exists.
    // Handles sorting.
    get data() {
        this.selectedModuleData = orderBy(this.selectedModuleData, this.sort);

        if (this.search !== null && this.search.length > 1) {
            let sText = this.search.toLowerCase();
            this.skip = 0;

            return this.selectedModuleData.filter(
                (d) =>
                    d.name.toLowerCase().includes(sText) ||
                    d.description.toLowerCase().includes(sText) ||
                    d.moduleName.toLowerCase().includes(sText)
            );
        } else {
            return this.selectedModuleData;
        }
    }

    // Filters all report data to limit based on selected module on left-hand menu
    selectItem(item: ReportMenuModuleItems) {
        let currentSelectedItem = this.modules.findIndex((i) => i.selected == true);
        this.modules[currentSelectedItem].selected = false;

        item.selected = true;

        if (item.name.toLowerCase() == 'all reports') {
            this.selectedModuleData = this.allData;
        } else if (item.name.toLowerCase() == 'bookmarked') {
            this.selectedModuleData = this.allData.filter((d) => d.bookmarked == true);
        } else {
            this.selectedModuleData = this.allData.filter(
                (d) => d.moduleName.toLowerCase() == item.name.toLowerCase()
            );
        }

        this.updateGrid();
    }

    // Filters all report data to limit based on module selected in router
    setModuleFromRoute(module: string) {
        let moduleItem: ReportMenuModuleItems;

        switch (module.toLowerCase()) {
            case 'pipeline':
                moduleItem = this.modules.find((m) => m.name == 'Pipeline');
                break;
            case 'production':
                moduleItem = this.modules.find((m) => m.name == 'Production');
                break;
            case 'performance':
                moduleItem = this.modules.find((m) => m.name == 'Performance');
                break;
            case 'historical-trends':
                moduleItem = this.modules.find((m) => m.name == 'Historical Trends');
                break;
            case 'financials':
                moduleItem = this.modules.find((m) => m.name == 'Financials');
                break;
            case 'leads-partners':
                moduleItem = this.modules.find((m) => m.name == 'Leads & Partners');
                break;
            case 'bookmarked':
                moduleItem = this.modules.find((m) => m.name == 'Bookmarked');
                break;
            case 'directory':
                moduleItem = this.modules.find((m) => m.name == 'Directory');
                break;
            default:
                moduleItem = this.modules.find((m) => m.name == 'All Reports');
                break;
        }

        this.selectItem(moduleItem);
    }

    // Returns friendly router name for selected module (if passed through router)
    getRouterModuleName() {
        let returnName;
        switch (this.routerModule.toLowerCase()) {
            case 'historical-trends':
                returnName = 'historical trends';
                break;
            case 'leads-partners':
                returnName = 'leads & partners';
                break;
            default:
                returnName = this.routerModule;
                break;
        }
        return returnName + ' Home';
    }

    // Update values shown in grid
    updateGrid() {
        this.gridView = {
            data: this.data.slice(this.skip, this.skip + this.pageSize),
            total: this.data.length,
        };
    }

    // Counts total reports in each module for left-hand menu
    getItemTotals() {
        this.modules.forEach((e) => {
            let name = e.name.toLowerCase();
            if (name !== 'all reports' && name !== 'bookmarked') {
                e.count = this.allData.filter(
                    (d) => d.moduleName.toLowerCase() == e.name.toLowerCase()
                ).length;
            } else if (name == 'all reports') {
                e.count = this.allData.length;
            } else if (name == 'bookmarked') {
                e.count = this.allData.filter((d) => d.bookmarked == true).length;
            }
        });

        this.modules = this.modules.filter(
            (x) => x.count != 0 || x.name == 'All Reports' || x.name == 'Bookmarked'
        );
        this.doneLoadingModules = true;
    }

    determineIfLocked() {}

    isLocked(m: ReportMenuModuleItems) {
        return m.locked == true;
    }

    // Sets CSS icon for module data on each report item in grid
    getReportModuleIcon(item: ReportMenuData) {
        let css;
        switch (item.moduleName.toLowerCase()) {
            case 'leads & partners':
                css = 'fas fa-handshake leads-partners';
                break;
            case 'pipeline':
                css = 'fas fa-bolt pipeline';
                break;
            case 'production':
                css = 'fas fa-rocket production';
                break;
            case 'historical trends':
                css = 'fas fa-chart-line';
                break;
            case 'financials':
                css = 'fas fa-dollar-sign financials';
                break;
            case 'performance':
                css = 'fas fa-heartbeat performance';
                break;
            case 'directory':
                css = 'fas fa-address-book';
                break;
            default:
                css = '';
                break;
        }

        return css;
    }

    // Handle click event for bookmark icon.
    // Saves or deletes ReportUserBookmark record for user.
    // Updates item totals on left-menu.
    clickBookmark(item: ReportMenuData) {
        let currentStatus = item.bookmarked;
        item.bookmarked = !currentStatus;

        let data: ReportMenuBookmarkForm = {
            reportKey: item.reportKey,
            moduleKey: item.moduleKey,
            bookmarked: item.bookmarked,
        };

        this._reportService.updateReportBookmark(data).subscribe();
        this.getItemTotals();
    }

    pageChange(e: PageChangeEvent): void {
        this.skip = e.skip;
        this.pageSize = e.take;
        this.updateGrid();
    }

    sortChange(sortItemList: SortDescriptor[]): void {
        let oldSortList: SortDescriptor[] = this.sort.map((obj) => ({
            ...obj,
        }));

        let newSortList: SortDescriptor[] = [];
        this.sort = [];

        this.preservedSort.forEach((sortItem) => {
            let oldPreserved = sortItemList.find((item) => item.field === sortItem.field);

            if (oldPreserved) {
                newSortList.push(Object.assign({}, oldPreserved));
            }

            oldSortList = oldSortList.filter((itm) => itm.field != sortItem.field);
            sortItemList = sortItemList.filter((itm) => itm.field != sortItem.field);
        });

        oldSortList.forEach((oldSort) => {
            let itemToRemove = sortItemList.find(
                (ite) => ite.field === oldSort.field && ite.dir === oldSort.dir
            );

            if (itemToRemove) {
                sortItemList = sortItemList.filter((i) => i.field != itemToRemove.field);
            }
        });

        sortItemList.forEach((sort) => {
            newSortList.push(sort);
        });

        this.sort = newSortList.map((obj) => ({ ...obj }));
        this.updateGrid();
    }

    showTooltip(e: MouseEvent): void {
        const element = e.target as HTMLElement;

        if (
            ((element.nodeName === 'TD' || element.nodeName === 'TH' || element.nodeName === 'A') &&
                element.offsetWidth < element.scrollWidth) ||
            element.className === 'truncate'
        ) {
            this.tooltipDir.toggle(element);
        } else {
            this.tooltipDir.hide();
        }
    }

    getQueryparams(dataItem: ReportMenuData) {
        let queryParams = { navFromUrl: this.router.url };
        // NOTE: Add url spesific query params here
        let optionalparams = [
            {
                urlSegment: 'historical-trends',
                paramName: 'milestonekey',
                value: dataItem.milestoneKey,
            },
            {
                urlSegment: 'purchases',
                paramName: 'purpose',
                value: 'purchase',
            },
            {
                urlSegment: 'government',
                paramName: 'loanType',
                value: 'govt',
            },
        ];

        optionalparams.forEach((item) => {
            if (dataItem.routerPath.toLowerCase().search(item.urlSegment) > -1) {
                queryParams[item.paramName] = item.value;
            }
        });
        return queryParams;
    }

    cellClickHandler(event) {
        //this._routerExtra.setReferingFromUrl(this.router.url);
        if (event && event.dataItem) {
            if (event.column.field === 'bookmarked') {
                this.clickBookmark(event.dataItem);
            } else if (event.dataItem.routerPath) {
                this.router.navigate([event.dataItem.routerPath], {
                    queryParams: this.getQueryparams(event.dataItem),
                });
            } else {
                let module = '';
                switch (event.dataItem.moduleName.toLowerCase()) {
                    case 'leads & partners':
                        module = 'leads-partners';
                        break;
                    case 'historical trends':
                        module = 'historical-trends';
                        break;
                    default:
                        module = event.dataItem.moduleName.toLowerCase();
                        break;
                }

                this.router.navigate([module + '/upgrade']);
            }
        }
    }
}
